import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../firebase";
import { apiClient } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useBooking } from "../../context/BookingContext";

const WhatsAppResume = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ensureSession } = useAuth();
  const {
    setFlightData,
    setRevalId,
    setSessionId,
    setPassengerCounts,
    setTripType,
    setDepartureDate,
    setReturnDate,
    setMulticitySegmentDates,
  } = useBooking();

  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setError("This link is missing its access token. Please request a new one on WhatsApp.");
      return;
    }

    (async () => {
      try {
        const res = await apiClient.post("/whatsapp-handoff/exchange", { token });
        const {
          customToken,
          flightData,
          tripType,
          departureDate,
          returnDate,
          multicitySegmentDates,
          passengerCounts,
        } = res.data;

        const cred = await signInWithCustomToken(auth, customToken);
        const ok = await ensureSession(cred.user);
        if (!ok) throw new Error("Failed to establish a session");

        setFlightData(flightData);
        setRevalId(flightData?.revalId || "");
        setSessionId(flightData?.sessionId || "");
        setPassengerCounts(passengerCounts);
        setTripType(tripType);
        setDepartureDate(departureDate);
        setReturnDate(returnDate);
        setMulticitySegmentDates(multicitySegmentDates);

        navigate("/passenger-details", {
          replace: true,
          state: {
            flightData,
            passengerCounts,
            departureDate,
            returnDate,
            multicitySegmentDates,
            tripType,
          },
        });
      } catch (e: any) {
        const message =
          e?.response?.data?.errors?.[0]?.message ||
          e?.response?.data?.error ||
          "This link has expired or was already used. Please request a new one on WhatsApp.";
        setError(message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Link unavailable</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Loading your booking...</p>
      </div>
    </div>
  );
};

export default WhatsAppResume;
